/** What the deeper analysis needs from one graded answer. */
export interface AnalysisAnswer {
  questionType: string;
  questionText?: string;
  isCorrect: boolean;
  answered: boolean;
  itemsCorrect?: number;
  itemsTotal?: number;
  gradingPending?: boolean;
  /** Display text of the learner's answer and of the key; the key is absent when the author hides it. */
  answerText: string;
  correctAnswerText?: string;
  explanation?: string;
  sectionTitle?: string;
  /** Seconds on the question, as the runner reported it; 0 or absent when it did not. */
  timeSpent?: number;
  /** Marked for review. */
  flagged?: boolean;
}

export interface MistakeRow {
  /** 1-based position in the review, to jump to. */
  n: number;
  prompt: string;
  yourAnswer: string;
  correctAnswer: string;
  explanation: string;
  /** Left blank rather than answered wrong. */
  skipped: boolean;
  /** A passage or other multi-item question: only part of it is wrong, so the single answer pair would mislead. */
  partial: boolean;
  itemsWrong: number;
}

const stripTags = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** The questions that lost points, in review order. Passages carry a count only: their keys are per sub-question. */
export function mistakeList(answers: AnalysisAnswer[]): MistakeRow[] {
  const rows: MistakeRow[] = [];
  answers.forEach((a, i) => {
    if (a.gradingPending) return;
    const total = a.itemsTotal ?? 1;
    const itemsWrong = total - (a.itemsCorrect ?? (a.isCorrect ? 1 : 0));
    if (itemsWrong <= 0) return;
    rows.push({
      n: i + 1,
      prompt: stripTags(a.questionText ?? ''),
      yourAnswer: a.answered ? a.answerText : '',
      correctAnswer: a.correctAnswerText ?? '',
      explanation: stripTags(a.explanation ?? ''),
      skipped: !a.answered,
      partial: total > 1,
      itemsWrong,
    });
  });
  return rows;
}

/** A question that kept the student this long is worth a second look. */
export const LONG_QUESTION_SECONDS = 120;

export interface QuestionTiming {
  /** Mean seconds per timed question, per section, in the order the sections arrive. */
  averages: Array<{ title: string; seconds: number }>;
  /** Questions that took at least LONG_QUESTION_SECONDS, and how many of those were wrong. */
  long: { count: number; wrong: number };
  /** Questions marked for review, and how they turned out. */
  flagged: { count: number; correct: number; wrong: number };
}

/**
 * Where the time went and what was marked for review, from what the runner reported. Null when it reported neither
 * (an attempt from before it did, a runner that does not, or a locked review).
 */
export function questionTiming(answers: AnalysisAnswer[]): QuestionTiming | null {
  const timed = answers.filter((a) => a.answered && (a.timeSpent ?? 0) > 0);
  const flagged = answers.filter((a) => a.flagged);
  if (!timed.length && !flagged.length) return null;

  // A Reading passage is one answer holding several questions, and the runner times the passage: its time is shared
  // across its questions so "per question" stays true.
  const items = (a: AnalysisAnswer) => Math.max(a.itemsTotal ?? 1, 1);
  const perItem = (a: AnalysisAnswer) => (a.timeSpent ?? 0) / items(a);
  const bySection = new Map<string, { total: number; count: number }>();
  for (const a of timed) {
    const key = a.sectionTitle ?? '';
    const entry = bySection.get(key) ?? { total: 0, count: 0 };
    entry.total += a.timeSpent ?? 0;
    entry.count += items(a);
    bySection.set(key, entry);
  }
  const long = timed.filter((a) => perItem(a) >= LONG_QUESTION_SECONDS);
  return {
    averages: Array.from(bySection, ([title, v]) => ({ title, seconds: Math.round(v.total / v.count) })),
    long: { count: long.length, wrong: long.filter((a) => !a.isCorrect).length },
    flagged: { count: flagged.length, correct: flagged.filter((a) => a.isCorrect).length, wrong: flagged.filter((a) => !a.isCorrect).length },
  };
}
